import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, eur } from "../components/common";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { X, Trash2, Plus, CheckCircle2, AlertCircle, Ghost, Archive, ArchiveRestore, History, RefreshCw, CheckSquare, Square, Pencil } from "lucide-react";
import PlatformFilter from "../components/PlatformFilter";
import { useAutoRefresh } from "../lib/autoRefresh";

const NEXT = {
  trial: ["ativa", "cancelada"],
  ativa: ["em_atraso", "suspensa", "cancelada"],
  em_atraso: ["ativa", "suspensa"],
  suspensa: ["ativa", "cancelada"],
};

export default function Subscriptions() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const [toDelete, setToDelete] = useState(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [showOrfas, setShowOrfas] = useState(false);
  const [orfaCount, setOrfaCount] = useState(0);
  const [showDrift, setShowDrift] = useState(false);
  const [driftHistory, setDriftHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [planModal, setPlanModal] = useState(null); // {sub, plans}
  const [allPlans, setAllPlans] = useState([]);
  const plataforma = params.get("plataforma");
  const load = () => api.get(`/subscriptions?include_orfas=${showOrfas}`).then((r) => setRows(r.data)).catch(() => {});
  const loadOrfaCount = () => api.get("/subscriptions/orfas").then((r) => setOrfaCount(r.data.count || 0)).catch(() => {});
  const loadDrift = () => api.get("/subscriptions/drift-history?limit=25").then((r) => setDriftHistory(r.data.history || [])).catch(() => {});
  useEffect(() => { load(); }, [showOrfas]);
  useAutoRefresh(load);
  useEffect(() => {
    loadOrfaCount(); loadDrift();
    api.get("/plans").then((r) => setAllPlans(r.data || [])).catch(() => {});
  }, []);

  // hidrata filtro persistido da topbar quando nao ha param na URL
  useEffect(() => {
    if (!params.get("plataforma")) {
      const stored = localStorage.getItem("sh_search_platform");
      if (stored) setParams({ plataforma: stored });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setPlatform = (v) => {
    const p = new URLSearchParams(params);
    if (v) p.set("plataforma", v);
    else p.delete("plataforma");
    setParams(p);
  };

  const archiveSub = async (r) => {
    if (!window.confirm(`Arquivar definitivamente "${r.organizacao_nome}"? A subscricao deixa de aparecer na lista principal mas o historico e preservado.`)) return;
    try {
      await api.post(`/subscriptions/${r.id}/archive`);
      toast.success("Subscricao arquivada");
      load(); loadOrfaCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unarchiveSub = async (r) => {
    try {
      await api.post(`/subscriptions/${r.id}/unarchive`);
      toast.success("Subscricao restaurada");
      load(); loadOrfaCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const daysSinceOrfa = (r) => {
    if (!r.orfa_desde) return 0;
    const d = (Date.now() - new Date(r.orfa_desde).getTime()) / 86400000;
    return Math.floor(d);
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/subscriptions/sync-all");
      const conns = Object.entries(data.connectors || {});
      const ok = conns.filter(([, v]) => v.ok).length;
      toast.success(`Sincronizacao concluida: ${ok}/${conns.length} conectores`);
      load(); loadOrfaCount(); loadDrift();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAllVisibleOrfas = () => {
    const orfaIds = rows.filter((r) => r.orfa && !r.arquivada).map((r) => r.id);
    setSelectedIds((s) => {
      const allSel = orfaIds.every((id) => s.has(id));
      return allSel ? new Set() : new Set(orfaIds);
    });
  };
  const bulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return toast.error("Nada selecionado.");
    if (!window.confirm(`Arquivar ${ids.length} subscricao(oes) orfa(s)?`)) return;
    try {
      const { data } = await api.post("/subscriptions/bulk-archive", { sub_ids: ids });
      toast.success(`Arquivadas ${data.archived_count} de ${ids.length} (${data.skipped_count} ignoradas).`);
      setSelectedIds(new Set());
      load(); loadOrfaCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const transition = async (id, estado) => {
    try {
      await api.post(`/subscriptions/${id}/transition?estado=${estado}`);
      toast.success(`Subscricao -> ${estado.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/subscriptions/${toDelete.id}`);
      toast.success("Subscricao eliminada");
      if (data.connector) {
        toast.message("Nota: e uma subscricao via API. Se o tenant continuar na plataforma, sera recriada na proxima sincronizacao.");
      }
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = plataforma
    ? rows.filter((r) => (r.plataforma || "Interno") === plataforma)
    : rows;

  const canDelete = can("*");
  const canProvision = can("*");

  const orfaSelectableIds = useMemo(
    () => rows.filter((r) => r.orfa && !r.arquivada).map((r) => r.id), [rows]);
  const allOrfasSelected = orfaSelectableIds.length > 0
    && orfaSelectableIds.every((id) => selectedIds.has(id));

  const selectCol = {
    key: "select",
    label: (
      <button onClick={toggleSelectAllVisibleOrfas} data-testid="select-all-orfas"
        className="text-navy hover:text-blue" title="Selecionar todos os orfaos">
        {allOrfasSelected && orfaSelectableIds.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
      </button>
    ),
    render: (r) => (
      r.orfa && !r.arquivada ? (
        <button onClick={() => toggleSelect(r.id)} data-testid={`select-${r.id}`}
          className={`p-1 rounded ${selectedIds.has(r.id) ? "text-amber-700" : "text-slate-400"} hover:text-navy`}>
          {selectedIds.has(r.id) ? <CheckSquare size={14} /> : <Square size={14} />}
        </button>
      ) : <span className="text-slate-300 text-xs">-</span>
    ),
  };

  const cols = [
    { key: "organizacao_nome", label: "Cliente", render: (r) => <span className="font-semibold text-navy">{r.organizacao_nome}</span> },
    { key: "plataforma", label: "Plataforma", render: (r) => <Badge value={r.plataforma || "Interno"} /> },
    { key: "plano_nome", label: "Plano", render: (r) => (
      <button
        onClick={() => setPlanModal({ sub: r })}
        data-testid={`sub-plan-${r.id}`}
        className="text-left group"
      >
        <span className="text-sm text-navy font-semibold group-hover:text-blue group-hover:underline transition-colors">
          {r.plano_nome || <span className="text-muted2 italic">Sem plano</span>}
        </span>
        {r.periodo && <span className="text-[10px] text-muted2 block">{r.periodo}</span>}
      </button>
    )},
    { key: "origem", label: "Origem", render: (r) => <span className="text-xs text-muted2">{r.connector ? `API (${r.connector})` : (r.origem || "-")}</span> },
    { key: "mrr", label: "MRR", render: (r) => (
      <span className="flex items-center gap-1">
        {r.is_demo ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold" data-testid={`sub-demo-badge-${r.id}`}>Demo</span> : eur(r.mrr)}
      </span>
    )},
    { key: "estado", label: "Estado", render: (r) => (
      r.arquivada ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-200 text-slate-600" title="Arquivada">
          <Archive size={11} /> Arquivada
        </span>
      ) : r.orfa ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-800" title={r.orfa_motivo || "Tenant apagado remotamente"}>
          <Ghost size={11} /> Orfa {r.orfa_desde && <span className="opacity-70">({daysSinceOrfa(r)}d)</span>}
        </span>
      ) : <Badge value={r.estado} />
    ) },
    {
      key: "acoes",
      label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap items-center">
          {r.orfa && !r.arquivada && daysSinceOrfa(r) >= 30 && (
            <button onClick={() => archiveSub(r)} data-testid={`sub-archive-${r.id}`} title="Arquivar (>30d orfa)"
              className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1">
              <Archive size={12} /> Arquivar
            </button>
          )}
          {r.arquivada && (
            <button onClick={() => unarchiveSub(r)} data-testid={`sub-unarchive-${r.id}`} title="Restaurar"
              className="text-xs px-2 py-1 rounded bg-slate-600 text-white hover:bg-slate-700 inline-flex items-center gap-1">
              <ArchiveRestore size={12} /> Restaurar
            </button>
          )}
          {!r.orfa && !r.arquivada && (NEXT[r.estado] || []).map((s) => (
            <button key={s} onClick={() => transition(r.id, s)} data-testid={`sub-${r.id}-${s}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors">
              {s.replace(/_/g, " ")}
            </button>
          ))}
          {canDelete && (
            <button onClick={() => setToDelete(r)} data-testid={`sub-delete-${r.id}`} title="Eliminar registo"
              className="text-xs p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader overline="Recorrencia" title="Subscricoes"
        action={
          <div className="flex gap-2 items-center">
            <button onClick={syncAll} disabled={syncing} data-testid="sync-all-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-tint text-navy hover:bg-tint disabled:opacity-60">
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
              {syncing ? "A sincronizar..." : "Sincronizar conectores"}
            </button>
            {canProvision && (
              <Btn onClick={() => setProvisionOpen(true)} testid="sub-provision-btn"><Plus size={16} /> Provisionar tenant</Btn>
            )}
          </div>
        }
      />
      {orfaCount > 0 && (
        <div className="mb-4 flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2" data-testid="orfa-banner">
          <Ghost className="text-amber-600 shrink-0" size={16} />
          <div className="text-sm text-amber-800 flex-1">
            <b>{orfaCount}</b> subscricao(es) sem correspondencia no conector externo (tenants apagados remotamente).
          </div>
          {showOrfas && selectedIds.size > 0 && (
            <button onClick={bulkArchive} data-testid="bulk-archive-btn"
              className="text-xs px-3 py-1 rounded-lg bg-amber-800 text-white font-semibold hover:bg-amber-900 inline-flex items-center gap-1">
              <Archive size={12} /> Arquivar {selectedIds.size} seleccionada(s)
            </button>
          )}
          <button onClick={() => { setShowOrfas((v) => !v); setSelectedIds(new Set()); }} data-testid="orfa-toggle"
            className="text-xs px-3 py-1 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700">
            {showOrfas ? "Esconder orfas" : "Ver orfas"}
          </button>
        </div>
      )}
      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="subs-platform-filter-bar">
        <span className="text-sm text-muted2">Plataforma:</span>
        <PlatformFilter value={plataforma} onChange={setPlatform} testid="subs-platform-filter" />
        {plataforma && (
          <>
            <button onClick={() => setPlatform("")} data-testid="clear-platform-filter"
              className="text-xs text-muted2 hover:text-navy underline">limpar</button>
            <span className="text-xs text-muted2">- {filtered.length} subscricoes</span>
          </>
        )}
      </div>
      <Table columns={showOrfas ? [selectCol, ...cols] : cols} rows={filtered} testid="subscriptions-table" />

      {/* Timeline Drift */}
      <div className="mt-6 bg-white border border-tint rounded-xl p-5" data-testid="drift-history-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History size={16} className="text-navy" />
            <h3 className="font-bold text-navy tracking-tight text-sm">Historico de sincronizacao (drift)</h3>
          </div>
          <button onClick={() => setShowDrift((v) => !v)} data-testid="drift-toggle"
            className="text-xs px-3 py-1 rounded-lg bg-tint text-navy font-semibold hover:bg-slate-200">
            {showDrift ? "Ocultar" : "Ver timeline"}
          </button>
        </div>
        {showDrift && (
          driftHistory.length === 0 ? (
            <div className="text-sm text-muted2 py-2">Ainda sem execucoes de sincronizacao registadas.</div>
          ) : (
            <ol className="relative border-l-2 border-tint pl-4 space-y-3" data-testid="drift-timeline">
              {driftHistory.map((h, i) => (
                <li key={i} data-testid={`drift-row-${i}`} className="relative">
                  <span className={`absolute -left-[9px] top-1 w-3 h-3 rounded-full border-2 border-white ${
                    h.new_orfas_count > 0 ? "bg-amber-500" : h.resurrected_count > 0 ? "bg-emerald-500" : "bg-slate-300"
                  }`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue">{h.connector}</span>
                    <span className="text-[10px] text-muted2">{h.created_at ? new Date(h.created_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                    {h.new_orfas_count > 0 && (
                      <span className="text-[10px] bg-amber-100 text-amber-800 font-semibold px-1.5 py-0.5 rounded">+{h.new_orfas_count} orfa(s)</span>
                    )}
                    {h.resurrected_count > 0 && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 font-semibold px-1.5 py-0.5 rounded">-{h.resurrected_count} ressurreicao(oes)</span>
                    )}
                    {h.alerted && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 font-semibold px-1.5 py-0.5 rounded">TG enviado</span>
                    )}
                    <span className="text-[10px] text-muted2 ml-auto">
                      {h.current_active} activos - {h.total_orfas_after} orfas totais
                    </span>
                  </div>
                  {h.new_orfas && h.new_orfas.length > 0 && (
                    <div className="text-[11px] text-muted2 mt-0.5 flex flex-wrap gap-1">
                      {h.new_orfas.slice(0, 6).map((o, j) => (
                        <span key={j} className="bg-slate-100 rounded px-1.5 py-0.5">
                          {o.organizacao_nome || o.external_ref}
                        </span>
                      ))}
                      {h.new_orfas.length > 6 && <span className="text-muted2">+{h.new_orfas.length - 6}</span>}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )
        )}
      </div>

      {toDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="sub-delete-modal">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-navy text-xl font-bold tracking-tight mb-2">Eliminar subscricao</h2>
            <p className="text-muted2 text-sm mb-6">
              Vai eliminar permanentemente a subscricao de <b className="text-navy">{toDelete.organizacao_nome}</b> ({toDelete.plataforma || "Interno"} - {toDelete.plano_nome || "-"}). Esta acao nao pode ser desfeita.
            </p>
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setToDelete(null)} testid="sub-delete-cancel">Cancelar</Btn>
              <Btn variant="danger" onClick={doDelete} testid="sub-delete-confirm">Eliminar</Btn>
            </div>
          </div>
        </div>
      )}

      {provisionOpen && (
        <ProvisionTenantModal onClose={() => setProvisionOpen(false)} onDone={() => { setProvisionOpen(false); load(); }} />
      )}

      {planModal && (
        <PlanSelectModal
          sub={planModal.sub}
          plans={allPlans}
          onClose={() => setPlanModal(null)}
          onSaved={() => { setPlanModal(null); load(); }}
        />
      )}
    </div>
  );
}


function ProvisionTenantModal({ onClose, onDone }) {
  const [platforms, setPlatforms] = useState([]);
  const [connector, setConnector] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [search, setSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    slug: "", plan: "", admin_email: "", periodo: "mensal",
    triage_mode: "cogniseg", triage_name: "", triage_email: "", triage_password: "",
  });
  const [slugState, setSlugState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/subscriptions/provisionable-platforms").then((r) => {
      setPlatforms(r.data || []);
      const first = (r.data || []).find((p) => p.configured);
      if (first) setConnector(first.connector);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  useEffect(() => {
    if (!connector) return;
    setAccountId("");
    api.get(`/subscriptions/eligible-accounts?connector=${connector}`)
      .then((r) => setAccounts(r.data || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    if (connector === "cognietica") {
      api.get("/cognietica/plans").then((r) => {
        const ps = r.data.plans || [];
        setPlans(ps);
        if (ps[0]) upd("plan", ps[0].key);
      }).catch(() => {});
    }
  }, [connector]);

  const selectedPlatform = platforms.find((p) => p.connector === connector);
  const selectedAccount = accounts.find((a) => a.id === accountId);

  const filteredAccounts = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return accounts;
    return accounts.filter((a) =>
      (a.nome || "").toLowerCase().includes(s)
      || (a.nif || "").toLowerCase().includes(s)
      || (a.codigo || "").toLowerCase().includes(s));
  }, [accounts, search]);

  const suggestSlug = (nome) =>
    (nome || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30);

  const pickAccount = (a) => {
    setAccountId(a.id);
    setSlugState(null);
    upd("slug", suggestSlug(a.nome));
    upd("admin_email", a.email || "");
    if (!form.triage_email && a.email) upd("triage_email", a.email);
  };

  const checkSlug = async () => {
    if (!form.slug || connector !== "cognietica") return;
    try {
      const { data } = await api.get(`/cognietica/tenants/slug-check/${form.slug}`);
      setSlugState(data.available ?? data.is_available ?? null);
    } catch {
      setSlugState(null);
    }
  };

  const triageOk = form.triage_mode === "cogniseg" ||
    (form.triage_mode === "client" && form.triage_name && form.triage_email && (form.triage_password || "").length >= 8);
  const canSubmit = selectedAccount && selectedAccount.elegivel
    && form.slug && form.plan && form.admin_email && triageOk && !saving;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Preencha todos os campos obrigatorios");
      return;
    }
    const payload = {
      connector, account_id: accountId,
      slug: form.slug, plan: form.plan, admin_email: form.admin_email,
      periodo: form.periodo, triage_mode: form.triage_mode,
    };
    if (form.triage_mode === "client") {
      payload.triage_name = form.triage_name;
      payload.triage_email = form.triage_email;
      payload.triage_password = form.triage_password;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/subscriptions/provision-tenant", payload);
      setResult(data);
      toast.success("Tenant provisionado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="sub-provision-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Provisionamento manual</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">Novo tenant a partir de um cliente</h2>
          </div>
          <button onClick={onClose} data-testid="sub-provision-close"><X className="text-muted2" /></button>
        </div>

        {!result ? (
          <>
            <label className="block text-sm font-semibold text-navy mb-1">Plataforma</label>
            <select value={connector} onChange={(e) => setConnector(e.target.value)} data-testid="sub-provision-connector"
              className="w-full mb-4 px-3 py-2 border border-tint rounded-lg">
              <option value="">Selecionar...</option>
              {platforms.map((p) => (
                <option key={p.connector} value={p.connector} disabled={!p.configured}>
                  {p.label}{!p.configured ? " (nao configurada)" : ""}
                </option>
              ))}
            </select>
            {selectedPlatform && !selectedPlatform.configured && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mb-4">
                Configure a integracao em {selectedPlatform.platform_name} - Configuracao antes de provisionar.
              </div>
            )}

            {connector && (
              <>
                <label className="block text-sm font-semibold text-navy mb-1">Cliente</label>
                <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sub-provision-search"
                  placeholder="Procurar por nome, NIF ou codigo..."
                  className="w-full mb-2 px-3 py-2 border border-tint rounded-lg" />
                <div className="border border-tint rounded-lg max-h-56 overflow-y-auto mb-4" data-testid="sub-provision-accounts">
                  {filteredAccounts.length === 0 && (
                    <div className="p-3 text-sm text-muted2">Sem clientes elegiveis. Um cliente elegivel tem NIF, email e sem faturas em aberto.</div>
                  )}
                  {filteredAccounts.map((a) => {
                    const chosen = accountId === a.id;
                    return (
                      <button key={a.id} type="button" onClick={() => a.elegivel && pickAccount(a)}
                        data-testid={`sub-provision-acc-${a.id}`}
                        disabled={!a.elegivel}
                        className={`w-full text-left px-3 py-2 border-b border-tint last:border-b-0 flex items-center justify-between gap-3 ${
                          chosen ? "bg-tint" : a.elegivel ? "hover:bg-rowalt" : "opacity-50 cursor-not-allowed"
                        }`}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-navy truncate">
                            {a.nome} {a.codigo && <span className="text-xs text-muted2">({a.codigo})</span>}
                          </div>
                          <div className="text-xs text-muted2 truncate">
                            NIF {a.nif || "-"} - {a.email || "sem email"}
                          </div>
                          {!a.elegivel && (
                            <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} /> {a.motivos.join(" - ")}
                            </div>
                          )}
                        </div>
                        {a.elegivel ? (
                          <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                        ) : (
                          <AlertCircle size={16} className="text-red-500 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {selectedAccount && selectedAccount.elegivel && connector === "cognietica" && (
              <div className="border-t border-tint pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Slug (canal publico)</label>
                    <div className="flex gap-2">
                      <input value={form.slug}
                        onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                        onBlur={checkSlug} data-testid="sub-provision-slug"
                        className="flex-1 px-3 py-2 border border-tint rounded-lg" />
                      <Btn variant="ghost" onClick={checkSlug} testid="sub-provision-slug-check">Verificar</Btn>
                    </div>
                    {slugState === true && <div className="text-xs text-green-600 mt-1">Disponivel</div>}
                    {slugState === false && <div className="text-xs text-red-600 mt-1">Ja em uso</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
                    <select value={form.plan} onChange={(e) => upd("plan", e.target.value)}
                      data-testid="sub-provision-plan"
                      className="w-full px-3 py-2 border border-tint rounded-lg">
                      {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Email do admin</label>
                    <input value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)}
                      data-testid="sub-provision-admin"
                      className="w-full px-3 py-2 border border-tint rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Periodo</label>
                    <select value={form.periodo} onChange={(e) => upd("periodo", e.target.value)}
                      className="w-full px-3 py-2 border border-tint rounded-lg">
                      <option value="mensal">Mensal</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold mb-2">Gestor de Triagem</div>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => upd("triage_mode", "cogniseg")}
                      data-testid="sub-provision-triage-cogniseg"
                      className={`text-left border rounded-lg p-3 ${form.triage_mode === "cogniseg" ? "border-blue bg-tint" : "border-tint hover:border-blue"}`}>
                      <div className="text-sm font-semibold text-navy">Servico CogniSeg</div>
                      <div className="text-xs text-muted2 mt-1">A CogniSeg gere a triagem.</div>
                    </button>
                    <button type="button" onClick={() => upd("triage_mode", "client")}
                      data-testid="sub-provision-triage-client"
                      className={`text-left border rounded-lg p-3 ${form.triage_mode === "client" ? "border-blue bg-tint" : "border-tint hover:border-blue"}`}>
                      <div className="text-sm font-semibold text-navy">Do proprio cliente</div>
                      <div className="text-xs text-muted2 mt-1">Gestor interno do cliente.</div>
                    </button>
                  </div>
                  {form.triage_mode === "client" && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-sm font-semibold text-navy mb-1">Nome</label>
                        <input value={form.triage_name} onChange={(e) => upd("triage_name", e.target.value)}
                          data-testid="sub-provision-triage-name"
                          className="w-full px-3 py-2 border border-tint rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-navy mb-1">Email</label>
                        <input value={form.triage_email} onChange={(e) => upd("triage_email", e.target.value)}
                          data-testid="sub-provision-triage-email"
                          className="w-full px-3 py-2 border border-tint rounded-lg" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-semibold text-navy mb-1">Password (min 8)</label>
                        <input type="password" value={form.triage_password}
                          onChange={(e) => upd("triage_password", e.target.value)}
                          data-testid="sub-provision-triage-pwd"
                          className="w-full px-3 py-2 border border-tint rounded-lg" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end mt-6">
              <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
              <Btn onClick={submit} disabled={!canSubmit} testid="sub-provision-submit">
                {saving ? "A provisionar..." : "Criar tenant"}
              </Btn>
            </div>
          </>
        ) : (
          <div>
            <div className="bg-green-50 text-green-700 rounded-lg p-4 text-sm mb-4">
              Tenant provisionado com sucesso.
            </div>
            <div className="space-y-2 text-sm mb-6">
              <div>
                <span className="text-muted2">Canal publico:</span>{" "}
                <a className="text-blue" href={result.public_report_url || result.tenant?.public_report_url}
                   target="_blank" rel="noreferrer">
                  {result.public_report_url || result.tenant?.public_report_url}
                </a>
              </div>
              {(result.admin_password || result.admin?.password) && (
                <div className="bg-tint rounded-lg p-3">
                  <div className="text-muted2 text-xs mb-1">Credenciais do admin (aparece so uma vez):</div>
                  <div><b>{result.admin_email || result.admin?.email || form.admin_email}</b></div>
                  <div>Password: <code>{result.admin_password || result.admin?.password}</code></div>
                </div>
              )}
              {result.triage_manager && (
                <div className="bg-tint rounded-lg p-3" data-testid="sub-provision-triage-result">
                  <div className="text-muted2 text-xs mb-1">
                    Gestor de Triagem ({result.triage_manager.mode === "client" ? "cliente" : "Servico CogniSeg"}):
                  </div>
                  <div><b>{result.triage_manager.name}</b></div>
                  <div className="text-xs">{result.triage_manager.email}</div>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Btn onClick={onDone} testid="sub-provision-done">Concluir</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


const PERIODO_LABEL = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };
const MENSAL_KEY = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };

function PlanSelectModal({ sub, plans, onClose, onSaved }) {
  const [selectedPlanId, setSelectedPlanId] = useState(sub.plano_id || "");
  const [periodo, setPeriodo] = useState(sub.periodo || "anual");
  const [saving, setSaving] = useState(false);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const periodos = selectedPlan?.periodos_disponiveis || ["anual"];
  const mensalKey = MENSAL_KEY[periodo] || "preco_mensal_1ano";
  const mrr = selectedPlan ? (selectedPlan[mensalKey] || 0) : 0;

  const save = async () => {
    if (!selectedPlanId) return toast.error("Selecione um plano.");
    setSaving(true);
    try {
      await api.patch(`/subscriptions/${sub.id}/plan`, { plano_id: selectedPlanId, periodo });
      toast.success("Plano atualizado e MRR recalculado");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="plan-select-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Alterar plano</div>
            <h2 className="text-navy text-lg font-bold tracking-tight">{sub.organizacao_nome}</h2>
          </div>
          <button onClick={onClose} data-testid="plan-select-close"><X className="text-muted2" /></button>
        </div>

        <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
        <select value={selectedPlanId} onChange={(e) => { setSelectedPlanId(e.target.value); setPeriodo("anual"); }}
          data-testid="plan-select-dropdown"
          className="w-full mb-4 px-3 py-2 border border-tint rounded-lg text-sm">
          <option value="">- Selecionar plano -</option>
          {plans.filter((p) => p.ativo).map((p) => (
            <option key={p.id} value={p.id}>{p.nome} ({p.sku})</option>
          ))}
        </select>

        {selectedPlan && (
          <>
            <label className="block text-sm font-semibold text-navy mb-1">Periodo de compromisso</label>
            <div className="flex gap-2 mb-4">
              {periodos.map((per) => (
                <button key={per} onClick={() => setPeriodo(per)}
                  data-testid={`plan-select-periodo-${per}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    periodo === per ? "border-blue bg-blue text-white" : "border-tint bg-white text-navy hover:border-blue"
                  }`}>
                  {PERIODO_LABEL[per] || per}
                </button>
              ))}
            </div>

            <div className="bg-tint rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted2 text-xs">MRR (mensal)</span>
                  <div className="text-navy font-bold text-lg" data-testid="plan-select-mrr">{eur(mrr)}/mes</div>
                </div>
                <div>
                  <span className="text-muted2 text-xs">ARR (anual)</span>
                  <div className="text-navy font-bold text-lg">{eur(mrr * 12)}/ano</div>
                </div>
              </div>
              {selectedPlan.max_utilizadores > 0 && (
                <div className="text-xs text-muted2 mt-2">{selectedPlan.max_utilizadores} utilizadores incluidos (user Cogniseg nao conta)</div>
              )}
              {selectedPlan.addon_triagem_mensal > 0 && (
                <div className="text-xs text-emerald-700 mt-1">+ {eur(selectedPlan.addon_triagem_mensal)}/mes add-on Triagem</div>
              )}
              {selectedPlan.addon_dpo_mensal > 0 && (
                <div className="text-xs text-emerald-700 mt-1">+ {eur(selectedPlan.addon_dpo_mensal)}/mes add-on DPO</div>
              )}
            </div>
          </>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-tint">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={saving || !selectedPlanId} testid="plan-select-save">
            {saving ? "..." : "Guardar"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
